import React from "react";
import {
  Menu,
  MenuProps,
  MenuItem,
  Button,
  Divider,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import DoneIcon from "@mui/icons-material/Done";
import {
  ExerciseAssetDto,
  ExerciseTagDto,
} from "@growth-machine-llc/stridist-api-client";
import { useUpdateLibraryExercise } from "./hooks/useUpdateLibraryExercise";
import { useQuery } from "@tanstack/react-query";
import ExerciseTagsService from "../../services/ExerciseTagsService";
import TagChip from "./tags/TagChip";
import { isEqual } from "lodash";
import { exerciseTagCategoryMultiSelectStyles } from "./exerciseLibraryStyles";
import { ALL_EXERCISE_TAGS_QUERY_KEY } from "./ExercisesTableScreen";
import TagMultiSelectFooter from "../tags/TagMultiSelectFooter";
import { TagType } from "../../constants";

const useStyles = makeStyles((theme) => ({
  tag: {
    padding: theme.spacing(0.5, 1.5),
    height: 22,
    "& > *": {
      fontSize: 14,
    },
    alignItems: "center",
  },
}));

export interface ExerciseTagMultiSelectProps
  extends Omit<MenuProps, "open" | "onClose"> {
  onClose?: () => void;
  exercise?: ExerciseAssetDto;
  anchorEl?: HTMLElement | null;
  onChangeSelectedOptions?: (selected: ExerciseTagDto[]) => void;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  initialSelected?: ExerciseTagDto[];
  showButton?: boolean;
  readonly?: boolean;
}

export function ExerciseTagMultiSelect(props: ExerciseTagMultiSelectProps) {
  const s = useStyles();
  const classes = exerciseTagCategoryMultiSelectStyles();
  const {
    onChangeSelectedOptions,
    onClose,
    open,
    setOpen,
    anchorEl,
    showButton = true,
    initialSelected = [],
    exercise,
    readonly = false,
  } = props;

  const [selectedOptions, setSelectedOptions] =
    React.useState<ExerciseTagDto[]>(initialSelected);

  React.useEffect(() => {
    if (!isEqual(selectedOptions, initialSelected)) {
      setSelectedOptions(initialSelected);
    }
  }, [initialSelected]);

  const { data: exerciseTags } = useQuery({
    queryKey: [ALL_EXERCISE_TAGS_QUERY_KEY],
    queryFn: ExerciseTagsService.getAllExerciseTags,
  });

  const { mutate: updateExercise } = useUpdateLibraryExercise(selectedOptions);

  const [localAnchorEl, setLocalAnchorEl] = React.useState<HTMLElement | null>(
    null,
  );
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const isOpen = open !== undefined ? open : Boolean(localAnchorEl);

  const handleClose = () => {
    setOpen?.(false);
    setLocalAnchorEl(null);
    onClose?.();
    if (exercise && !isEqual(selectedOptions, initialSelected)) {
      updateExercise({
        ...exercise,
        tagIds: selectedOptions.map((tag) => tag.id),
      });
    }
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (setOpen) {
      setOpen(true);
    } else {
      setLocalAnchorEl(event.currentTarget);
    }
  };

  const isSelected = (tag: ExerciseTagDto) =>
    selectedOptions.some((t) => t.id === tag.id);

  const toggleTag = (tag: ExerciseTagDto) => {
    if (readonly) {
      return;
    }
    const updated = isSelected(tag)
      ? selectedOptions.filter((t) => t.id !== tag.id)
      : [...selectedOptions, tag];

    setSelectedOptions(updated);
    onChangeSelectedOptions?.(updated);
  };

  return (
    <>
      {showButton && (
        <Button
          ref={buttonRef}
          className={classes.manageButton}
          children={"Manage tags"}
          onClick={handleButtonClick}
        />
      )}
      <Menu
        open={isOpen}
        anchorEl={anchorEl || localAnchorEl || buttonRef.current}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 300,
              maxHeight: 250,
            },
          },
        }}
        MenuListProps={{
          sx: {
            paddingBottom: 0,
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        className={classes.menu}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {!exerciseTags || exerciseTags.length === 0 ? (
          <MenuItem className={classes.menuHeader}>
            No existed exercise tags
          </MenuItem>
        ) : (
          [
            <MenuItem key="header" className={classes.menuHeader}>
              Select tags
            </MenuItem>,
            <Divider key="divider-0" />,
            ...exerciseTags.flatMap((tag, idx) => {
              const selected = isSelected(tag);
              return [
                <Box
                  key={`box-${tag.id}`}
                  className={classes.tagOption}
                  onClick={() => toggleTag(tag)}
                >
                  <Box
                    sx={{
                      width: 24,
                      display: "inline-flex",
                      justifyContent: "center",
                      mr: 1,
                    }}
                  >
                    {selected && <DoneIcon color="primary" fontSize="small" />}
                  </Box>
                  <Box>
                    <TagChip
                      primaryColor={tag.primaryColor}
                      className={s.tag}
                      text={tag.title}
                    />
                    <Typography className={classes.tagDescription}>
                      {tag.description}
                    </Typography>
                  </Box>
                </Box>,
                <Divider key={`divider-${idx + 1}`} />,
              ];
            }),
          ]
        )}
        {!readonly && (
          <TagMultiSelectFooter
            isTagsExisting={exerciseTags?.length > 0}
            tagType={TagType.EXERCISE}
          />
        )}
      </Menu>
    </>
  );
}
